package fr.manyverse.app.presentation.dice3d

import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.atan2
import kotlin.math.ceil
import kotlin.math.cos
import kotlin.math.exp
import kotlin.math.sin
import kotlin.math.sqrt
import kotlin.random.Random

/**
 * Dimensions de l'arène en pixels et rayon (circumradius projeté) des dés.
 *
 * Les dés roulent dans toute l'arène mais se rangent dans la bande verticale
 * [settleTopFraction, settleBottomFraction] : sous le logo en filigrane du plateau et
 * au-dessus de la zone de texte des résultats.
 */
data class ArenaSpec(
    val width: Float,
    val height: Float,
    val dieRadius: Float,
    val wallInset: Float = 0f,
    val settleTopFraction: Float = 0f,
    val settleBottomFraction: Float = 0.65f,
    /**
     * Retrait du mur du fond, en pixels. Plus grand que [wallInset] parce que la caméra
     * regarde le plateau depuis un point situé sous son bord bas : un dé est d'autant plus
     * dessiné vers le haut de l'écran qu'il est haut au-dessus du feutre, et un dé collé au
     * fond penche alors **par-dessus** le bord du plateau. Le calcul de ce retrait appartient
     * au rendu, qui seul connaît la caméra (voir `arenaFor`).
     */
    val topInset: Float = wallInset
)

/**
 * Étapes de la vie d'un dé : il roule, bascule sur sa face quand son élan est épuisé, attend
 * les autres, puis rejoint la zone de lecture. Un dé à l'arrêt ne bouge donc jamais tant
 * qu'il patiente, et plus rien ne tourne une fois la place atteinte.
 */
enum class DiePhase { ROLLING, RESTING, GATHERING, SETTLED }

/** État mutable d'un dé pendant la simulation, lu directement par le rendu. */
class DieSimState internal constructor(
    val die: DisplayDie,
    var x: Float,
    var y: Float,
    /** Hauteur du centre du dé au-dessus du feutre, en pixels. */
    var z: Float,
    internal var vx: Float,
    internal var vy: Float,
    internal var vz: Float,
    var orientation: Quaternion,
    /** Vitesse angulaire propre (rad/s, repère du plateau) : le dé a sa propre inertie. */
    internal var omega: Vec3,
    /**
     * Hauteur du centre quand le dé, **dans son orientation courante**, repose sur le
     * feutre — la fonction d'appui de son maillage, mise à l'échelle du plateau.
     *
     * Elle n'est donc pas constante : posé à plat le centre descend à l'inradius, dressé
     * sur une arête il remonte au midradius. C'est ce relief qui fait qu'un polyèdre
     * culbute au lieu de rouler comme une bille — le sol se dérobe vers le haut sous le dé
     * qui bascule, le soulève, puis redescend et le laisse retomber sur la face suivante.
     */
    internal var groundHeight: Float,
    /**
     * Prise du feutre sur ce dé : multiplie la résistance au roulement. C'est ce qui fait
     * qu'un lancer dure plus longtemps qu'un autre.
     */
    internal val grip: Float
) {
    /**
     * Inverse des moments d'inertie principaux, à l'échelle du plateau (repère du dé). Sert à
     * convertir une impulsion appliquée au point de contact en changement de rotation.
     */
    internal var invInertia: Vec3 = Vec3.ZERO

    var phase: DiePhase = DiePhase.ROLLING
        internal set

    /**
     * Pose **affichée** : celle du dé entre ses deux derniers pas de physique, au prorata du
     * temps qui reste en réserve (voir [DiceRollSimulation.step]). C'est elle que le rendu doit
     * lire, jamais [x], [y], [z] ou [orientation].
     *
     * Le pas de physique ne tombe pas juste sur l'image : à 90 Hz l'écran voit passer 3, 3 puis
     * 2 pas, et un dé lancé à pleine vitesse avançait de 30, 30 puis 20 px au lieu de 26
     * réguliers — une saccade très visible pendant la course. Montrer la pose
     * interpolée la fait disparaître, au prix d'un retard d'affichage inférieur à un pas.
     */
    var drawX: Float = x
        internal set
    var drawY: Float = y
        internal set
    var drawZ: Float = z
        internal set
    var drawOrientation: Quaternion = orientation
        internal set

    /** Pose au début du dernier pas de physique : l'autre borne de l'interpolation. */
    internal var previousX = x
    internal var previousY = y
    internal var previousZ = z
    internal var previousOrientation = orientation

    /** Fige la pose courante comme point de départ du pas qui commence. */
    internal fun rememberPose() {
        previousX = x
        previousY = y
        previousZ = z
        previousOrientation = orientation
    }

    /** Pose affichée à la fraction [alpha] du pas en cours, entre la précédente et la courante. */
    internal fun blendPose(alpha: Float) {
        if (alpha >= 1f) {
            // La pose exacte, et non une interpolation qui la renormalise au dernier chiffre près.
            drawX = x
            drawY = y
            drawZ = z
            drawOrientation = orientation
            return
        }
        drawX = previousX + (x - previousX) * alpha
        drawY = previousY + (y - previousY) * alpha
        drawZ = previousZ + (z - previousZ) * alpha
        drawOrientation = Quaternion.slerp(previousOrientation, orientation, alpha)
    }

    /**
     * Vrai dès que le dé a pénétré dans le plateau. Les dés sont lâchés **hors piste**, sur
     * un des trois bords qui font face au joueur : tant qu'ils n'ont pas franchi la limite,
     * les murs n'existent pas pour eux — sans quoi un dé rebondirait sur la paroi par
     * l'extérieur au lieu d'entrer. Le passage est irréversible : une fois dedans, il y reste.
     */
    var hasEnteredTray: Boolean = false
        internal set

    /** Vrai quand le dé touche le feutre — seul moment où le roulement contraint sa rotation. */
    val isGrounded: Boolean get() = z <= groundHeight + GROUND_EPSILON

    /** Instant (en secondes de simulation) où le dé a épuisé son élan. */
    var stoppedRollingAt: Float = Float.MAX_VALUE
        internal set

    internal var gatherElapsed = 0f
    internal var fromX = 0f
    internal var fromY = 0f
    internal var fromQ = Quaternion.IDENTITY
    internal var targetX = 0f
    internal var targetY = 0f

    /** Calculée à l'entrée en regroupement : le dé se redresse en rejoignant sa case. */
    internal var targetOrientation = Quaternion.IDENTITY

    /**
     * Face sur laquelle le dé s'est couché. Nulle tant qu'il roule : rien n'est décidé
     * d'avance, c'est la physique qui tranche.
     */
    var landedFace: Face? = null
        internal set

    /** Résultat du lancer : la valeur portée par [landedFace]. */
    val landedValue: Int? get() = landedFace?.value

    internal val speed: Float get() = sqrt(vx * vx + vy * vy)

    private companion object {
        const val GROUND_EPSILON = 1e-3f
    }
}

/**
 * Simulation déterministe d'un lancer : le dé roule dans l'arène avec rebonds sur les murs
 * et collisions entre dés.
 *
 * **Le dé est un solide rigide en contact avec un plan.** Il porte sa position, sa vitesse,
 * son orientation, sa vitesse angulaire et son tenseur d'inertie ([PolyhedronMesh.inertia]) ;
 * tout le reste — le roulement, la culbute, le couchage final — sort de la résolution du
 * contact, et n'est écrit nulle part.
 *
 * Le mécanisme tient en une phrase : **le feutre pousse le dé là où il le touche**
 * ([solveFeltContact]). Les sommets d'appui sont ceux que donne le maillage
 * ([PolyhedronMesh.support]) — un pour une pointe, deux pour une arête, trois ou plus pour une
 * face — et chacun reçoit une impulsion normale qui annule son enfoncement, plus une impulsion
 * de frottement de Coulomb bornée par cette charge. En découlent, sans une ligne de plus :
 *
 *  - **la culbute** : un appui décalé par rapport au centre crée un couple, donc le dé bascule
 *    par-dessus son arête au lieu de pivoter sur lui-même — et son centre décrit l'arc
 *    correspondant, parce que le frottement empêche le point d'appui de patiner ;
 *  - **le prix de la bascule** : hisser le centre par-dessus l'arête se paie en élan, et la
 *    pesanteur le lui reprend, sans qu'aucun barème n'ait à le dire ;
 *  - **le décollage** : le dé quitte le feutre dès que le contact cesserait de le pousser ;
 *  - **la déviation** : un dé qui accroche un coin de travers reçoit une impulsion hors de
 *    l'axe de sa course et part en biais, comme un vrai dé ;
 *  - **le couchage final** : un dé posé de travers a son centre hors de son polygone d'appui,
 *    le couple ne s'annule donc que face à plat. Rien n'est redressé à la main.
 *
 * La forme du solide décide de tout le reste : le d4 barillet, allongé, hisse son centre de
 * près de 0,6 rayon à chaque bascule quand le d20, presque rond, ne le lève que de 0,18 — le
 * premier paie donc ses culbutes trois fois plus cher, sans qu'aucun réglage ne le dise.
 *
 * **En l'air, c'est le moment cinétique qui se conserve, pas la vitesse angulaire**
 * ([advanceFreeSpin]) : les solides de Platon, isotropes, tournent d'un bloc, mais le barillet
 * du d4 et le trapézoèdre du d10 précessent — ils se dandinent autour de leur axe long comme
 * un vrai dé cristal.
 *
 * **Les dés sont lancés ensemble, pas tirés un par un.** Une poignée de dés quitte une seule
 * main : un unique côté, un unique cap et une unique vitesse sont tirés pour tout le jet, et
 * chaque dé n'en dévie que de la dispersion d'un lâcher ([DIE_HEADING_SPREAD],
 * [DIE_SPEED_JITTER]). Le lâcher se fait **hors piste**, n'importe où sur le demi-périmètre
 * qui fait face au joueur — du flanc gauche, en passant par le bas, jusqu'au flanc droit,
 * jamais par le haut — et les dés partent de front vers le cœur du plateau : ils **entrent**
 * dans la piste au lieu d'y apparaître. Comme leurs élans sont voisins, ils s'arrêtent aussi
 * plus près les uns des autres, au lieu de laisser les premiers arrivés figés à attendre.
 *
 * **La durée n'est pas fixée** : chaque dé roule tant qu'il lui reste de l'élan, puis
 * s'immobilise sur place dès qu'il est couché à plat et que sa vitesse tombe sous
 * [STOP_SPEED_FRACTION] de l'arène. Vitesse du jet et prise du feutre étant tirées au hasard,
 * un lancer peut être expédié en deux secondes comme s'étirer sur quatre. Ce n'est qu'une fois
 * **tous** les dés arrêtés qu'ils se regroupent ensemble dans la zone de lecture.
 *
 * **Le résultat n'est pas décidé d'avance** : le dé roule librement et c'est la face sur
 * laquelle le contact finit par le coucher qui donne la valeur ([landedValues]). Rien n'est
 * donc jamais à rattraper : le résidu d'inclinaison à l'arrêt est de l'ordre du degré, et le
 * regroupement l'absorbe au passage sans qu'aucun dé ne pivote sur place.
 *
 * **Équité.** La trajectoire dépend désormais de l'orientation — la hauteur d'appui, donc
 * les contacts, donc le frottement en découlent — et l'argument ne peut plus être qu'elle
 * en est indépendante. Il tient par la symétrie : la dynamique ne connaît du dé que son
 * maillage, donc partir de `q₀ · g` pour `g` dans le groupe de symétrie du solide produit
 * exactement la même trajectoire dans le monde, qui se couche sur la même face **du
 * monde** — mais sur une face **du dé** différente. Comme `q₀` est tiré uniformément sur
 * SO(3), `q₀ · g` l'est aussi, d'où `P(f) = P(g·f)` pour tout `g` ; ces solides étant
 * isoédriques, leur groupe est transitif sur les faces et chaque face est équiprobable.
 * (Vérifié par khi-deux dans `DiceRollSimulationTest`.)
 *
 * **Le pas d'intégration est fixe et découplé de l'affichage** ([FIXED_STEP]) : [step] accumule
 * le temps écoulé et le consomme par pas entiers. La physique est donc la même sur un écran
 * 60, 90 ou 120 Hz, et une image longue ne fait plus avancer le dé d'un bond — à 0,05 s de
 * pas, il parcourait 100 px et tournait d'un demi-tour d'un seul coup, sautant purement et
 * simplement la culbute que la fonction d'appui, échantillonnée une fois par pas, aurait dû
 * lui faire franchir.
 *
 * Tout l'aléa est tiré dans le constructeur : à [Random] identique, trajectoire identique.
 */
class DiceRollSimulation(
    dice: List<DisplayDie>,
    arena: ArenaSpec,
    private val gatherDuration: Float = GATHER_DURATION_SECONDS,
    random: Random
) {
    val states: List<DieSimState>

    /** Dimensions courantes du plateau : [resize] les ajuste sans interrompre le lancer. */
    var arena: ArenaSpec = arena
        private set

    var elapsedSeconds: Float = 0f
        private set

    /** Vrai dès que tous les dés sont regroupés et posés — il n'y a pas de durée cible. */
    val isSettled: Boolean get() = states.all { it.phase == DiePhase.SETTLED }

    /**
     * Faces obtenues, dans l'ordre des dés — vide tant qu'un dé roule encore. C'est la
     * physique qui produit le résultat du lancer, pas un tirage préalable.
     */
    val landedValues: List<Int>
        get() = states.mapNotNull { it.landedValue }.takeIf { it.size == states.size }.orEmpty()

    /**
     * Déroule la simulation jusqu'à l'arrêt sans passer par l'animation : utile quand
     * l'affichage est interrompu avant la fin.
     *
     * Le résultat est exactement celui que l'animation aurait donné, et pas seulement un
     * résultat plausible : le pas d'intégration ne dépendant plus de la cadence d'affichage
     * ([step]), dérouler la fin du lancer d'un coup revient au même calcul que la regarder.
     */
    fun fastForward() {
        var guard = 0
        while (!isSettled && guard < FAST_FORWARD_MAX_STEPS) {
            step(FAST_FORWARD_STEP)
            guard++
        }
        // Un lancer qu'on ne regarde plus ne se ressent pas : les chocs de ces quelques milliers
        // de pas ne doivent pas partir dans la main à la première image du prochain lancer.
        impactPeaks.fill(0f)
    }

    /**
     * Le choc le plus marquant survenu depuis le dernier appel, ou `null` si rien de sensible ne
     * s'est produit. Vide le tampon au passage.
     *
     * Un seul choc en sort par appel : deux impulsions qui se chevauchent dans un moteur haptique
     * ne font pas deux sensations, elles font de la bouillie. Le plus fort l'emporte, et les gains
     * par type ([FELT_IMPACT_GAIN] et ses voisins) sont déjà appliqués — la comparaison se fait
     * donc à ressenti égal, pas à vitesse égale.
     */
    fun drainImpact(): DiceImpact? {
        var best = 0
        for (index in 1 until impactPeaks.size) {
            if (impactPeaks[index] > impactPeaks[best]) best = index
        }
        val strength = impactPeaks[best]
        impactPeaks.fill(0f)
        if (strength <= 0f) return null
        return DiceImpact(DiceImpactKind.entries[best], strength)
    }

    /**
     * Retient [magnitude] — une vitesse de choc en pixels du plateau — comme candidat au pic de son
     * type. Le [gain] porte la couleur du contact : un bord claque plus fort qu'un feutre pour une
     * même vitesse d'approche.
     */
    private fun recordImpact(kind: DiceImpactKind, magnitude: Float, gain: Float) {
        if (magnitude <= 0f) return
        val strength = (magnitude * gain / impactReference).coerceAtMost(1f)
        if (strength < MIN_IMPACT_STRENGTH) return
        val index = kind.ordinal
        if (strength > impactPeaks[index]) impactPeaks[index] = strength
    }

    /**
     * Bord franchi par le jet : un flanc (vrai) ou le bord bas. Il désigne l'axe **transverse**
     * sur lequel la poignée doit rester dans la section du plateau tant qu'elle n'y est pas
     * entrée — voir [integrateRoll].
     */
    private var throwThroughSide = false

    /** Temps reçu par [step] mais pas encore consommé : toujours moins d'un [FIXED_STEP]. */
    private var pendingSeconds = 0f

    /**
     * Appui du dé en cours de traitement. Un seul tampon suffit : il est rempli puis consommé
     * dans la foulée, dé par dé, et la simulation tourne à 240 pas par seconde — y allouer
     * quoi que ce soit se paierait en ramasse-miettes.
     */
    private val contact = SupportContact()

    /**
     * Appui du dé en cours de traitement contre un mur. Distinct de [contact] : celui-ci doit
     * encore décrire l'appui sur le feutre quand [nudgeOffBalance] le relit, après les murs.
     */
    private val wallContact = SupportContact()

    /** Côté par lequel un dé en touche un autre ([resolveDieCollisions]). */
    private val probe = SupportContact()

    /** Charge accumulée par chaque sommet d'appui, et vitesse de séparation visée. */
    private val impulses = FloatArray(SupportContact.MAX_POINTS)
    private val targets = FloatArray(SupportContact.MAX_POINTS)

    private var stopSpeed = STOP_SPEED_FRACTION * minOf(arena.width, arena.height)
    private var gravity = GRAVITY_FACTOR * minOf(arena.width, arena.height)

    /**
     * Vitesse d'approche en deçà de laquelle un contact n'est plus un choc mais un appui : ce
     * que la pesanteur donne à un corps posé en quelques pas d'intégration. C'est la coupure
     * qui empêche un dé au repos de rebondir indéfiniment sur sa propre chute.
     */
    private var restingApproach = gravity * FIXED_STEP * RESTING_STEPS
    private var settleCells: List<Pair<Float, Float>> = settleGrid(dice.size)

    /**
     * Violence de choc valant pleine force. Elle suit la taille du plateau comme [gravity] et
     * [stopSpeed] : c'est ce qui fait qu'un même lancer se ressent pareil dans la piste de
     * l'onglet et dans l'overlay plein écran, alors que ses vitesses y sont deux fois plus grandes.
     */
    private var impactReference = IMPACT_REFERENCE_FACTOR * minOf(arena.width, arena.height)

    /**
     * Le choc le plus fort de chaque type depuis le dernier [drainImpact], indexé par
     * [DiceImpactKind.ordinal].
     *
     * La simulation avance à 240 pas par seconde et l'écran n'en montre qu'un sur quatre ; un
     * moteur haptique, lui, ne sait pas rejouer plus d'une impulsion toutes les quelques dizaines
     * de millisecondes. Émettre un événement par contact reviendrait donc à en jeter la quasi
     * totalité — et à jeter les plus marquants aussi souvent que les autres. On retient le **pic**
     * de chaque type et l'image qui vient prélève ce qui compte.
     */
    private val impactPeaks = FloatArray(DiceImpactKind.entries.size)

    init {
        val reference = minOf(arena.width, arena.height)
        val r = arena.dieRadius
        // Un seul geste pour toute la poignée : un côté, un cap, une vitesse, une hauteur.
        //
        // Le lâcher se fait n'importe où sur le demi-périmètre qui fait face au joueur : du
        // flanc gauche, en passant par le bas, jusqu'au flanc droit. L'angle est mesuré depuis
        // le centre du plateau vers l'extérieur, dans le repère écran (y vers le bas) : 0
        // sort à droite, π/2 en bas, π à gauche — jamais par le haut.
        val outward = random.nextFloat() * PI.toFloat()
        val outX = cos(outward)
        val outY = sin(outward)
        val centerX = arena.width / 2f
        val centerY = arena.height / 2f
        // Distance du centre au bord du plateau dans cette direction : le rayon touche le
        // flanc ou le bord bas selon l'angle, les coins se traitant d'eux-mêmes.
        val toSide = if (abs(outX) < 1e-4f) Float.MAX_VALUE else centerX / abs(outX)
        val toBottom = if (abs(outY) < 1e-4f) Float.MAX_VALUE else centerY / abs(outY)
        // Bord effectivement franchi : un flanc, ou le bord bas. Il décide de l'axe le long
        // duquel la poignée sort du plateau — sur l'autre, elle doit y rester.
        val throughSide = toSide < toBottom
        throwThroughSide = throughSide
        val reach = minOf(toSide, toBottom) + r * THROW_ENTRY_RADII
        val originX = centerX + outX * reach
        val originY = centerY + outY * reach

        val throwSpeed =
            (MIN_SPEED_FACTOR + random.nextFloat() * (MAX_SPEED_FACTOR - MIN_SPEED_FACTOR)) * reference
        val throwDrop = (MIN_DROP_FACTOR + random.nextFloat() * (MAX_DROP_FACTOR - MIN_DROP_FACTOR)) * r
        // Une poignée est **jetée**, pas déposée : elle quitte la main en descendant déjà.
        val throwDownward = THROW_DOWNWARD * sqrt(gravity * r)
        // La prise du feutre est celle de la piste, pas une propriété de chaque dé : elle est
        // commune à la poignée. La tirer par dé étalait les arrêts sur plus d'une seconde et
        // laissait les premiers arrivés figés à attendre les autres.
        val throwGrip = MIN_GRIP + random.nextFloat() * (MAX_GRIP - MIN_GRIP)

        // La poignée se range sur l'axe **transverse** au bord franchi : en largeur pour un
        // lâcher par le bas, en hauteur pour un lâcher par un flanc. Deux exigences s'y
        // croisent, et une seule des deux ne suffit pas :
        //  — l'écartement ne descend jamais sous le diamètre de collision, sinon les dés
        //    partent imbriqués et se repoussent d'un bond en arrivant sur le feutre ;
        //  — toute la rangée tient dans la section du plateau, sinon un dé d'aile naît
        //    au-delà d'un mur latéral, longe la paroi par l'extérieur et n'entre jamais.
        // Une poignée trop large se replie donc sur plusieurs rangs, et la rangée entière est
        // recentrée — la rogner dé par dé les tasserait les uns sur les autres.
        val minT = (if (throughSide) arena.topInset else arena.wallInset) + r
        val maxT = ((if (throughSide) arena.height else arena.width) - arena.wallInset - r)
            .coerceAtLeast(minT)
        val abreastPitch = THROW_ABREAST_PITCH * r
        val perRowFit = maxOf(1, ((maxT - minT) / abreastPitch).toInt() + 1)
        val rows = ceil(dice.size.toFloat() / perRowFit).toInt().coerceAtLeast(1)
        val perRow = ceil(dice.size.toFloat() / rows).toInt().coerceAtLeast(1)
        val halfRow = (perRow - 1) / 2f * abreastPitch
        val rayT = if (throughSide) originY else originX
        val baseT = if (maxT - minT >= 2f * halfRow) {
            rayT.coerceIn(minT + halfRow, maxT - halfRow)
        } else {
            (minT + maxT) / 2f
        }
        // Sens dans lequel la poignée s'éloigne du plateau, aligné sur le bord franchi.
        val outAxis = if (throughSide && outX < 0f) -1f else 1f
        states = dice.mapIndexed { index, die ->
            // Place dans la poignée : de front sur l'axe transverse, étagée en rangs vers
            // l'arrière pour que les dés ne franchissent pas le bord d'un seul bloc.
            val row = index / perRow
            val inThisRow = minOf(perRow, dice.size - row * perRow)
            val abreast = (index % perRow - (inThisRow - 1) / 2f) * abreastPitch
            val depth = row * THROW_ROW_PITCH * r +
                (random.nextFloat() - 0.5f) * THROW_TRAIL_JITTER * r
            val fromX = if (throughSide) originX + outAxis * depth else baseT + abreast
            val fromY = if (throughSide) baseT + abreast else originY + outAxis * depth
            // Chaque dé vise le cœur du plateau **depuis là où il est**, sans précision : un
            // jet, pas une visée. Un cap commun ferait suivre au dé le plus excentré une
            // parallèle qui longe la piste sans jamais y entrer.
            //
            // Mais viser tous le même point ferait converger la poignée en un tas : chaque dé
            // garde donc une part de son décalage dans sa cible ([THROW_AIM_KEEP]), et
            // l'éventail se resserre au lieu de se refermer.
            val keptX = if (throughSide) 0f else abreast * THROW_AIM_KEEP
            val keptY = if (throughSide) abreast * THROW_AIM_KEEP else 0f
            val aimX = (centerX + keptX + (random.nextFloat() - 0.5f) * arena.width * THROW_AIM_SPREAD)
                .coerceIn(arena.width * 0.25f, arena.width * 0.75f)
            val aimY = (centerY + keptY + (random.nextFloat() - 0.5f) * arena.height * THROW_AIM_SPREAD)
                .coerceIn(arena.height * 0.25f, arena.height * 0.75f)
            val angle = atan2(aimY - fromY, aimX - fromX) +
                (random.nextFloat() - 0.5f) * DIE_HEADING_SPREAD
            val speed = throwSpeed * (1f + (random.nextFloat() - 0.5f) * DIE_SPEED_JITTER)
            val vx = cos(angle) * speed
            val vy = sin(angle) * speed
            // Le rayon de roulement est celui du solide, pas un réglage commun : un d4 doit
            // tourner bien plus vite qu'un d12 pour parcourir la même distance.
            val rollRadius = (arena.dieRadius * die.mesh.rollingRadius).coerceAtLeast(1e-3f)
            // Culbutage initial : le roulement du lancer, plus une composante libre autour
            // d'un axe quelconque — comme un dé qui quitte la main en tournant sur lui-même.
            val tumbleRate =
                (MIN_TUMBLE_FACTOR + random.nextFloat() * (MAX_TUMBLE_FACTOR - MIN_TUMBLE_FACTOR)) *
                    speed / rollRadius
            // Tous quittent la main à peu près à la même hauteur et à la même vitesse de
            // descente : ils touchent le feutre presque ensemble, comme une poignée jetée d'un
            // coup. Les deux partagent le même écart parce qu'ils décrivent le même geste — un
            // dé lâché un peu plus haut l'est aussi un peu plus fort.
            val release = 1f + (random.nextFloat() - 0.5f) * DIE_DROP_JITTER
            val drop = throwDrop * release
            val orientation = Quaternion.random(random)
            DieSimState(
                die = die,
                x = fromX,
                y = fromY,
                // Le dé est lâché en l'air : il tombe sur le feutre et rebondit avant de rouler.
                z = groundHeightFor(die, orientation, arena.dieRadius) + drop,
                vx = vx,
                vy = vy,
                vz = -throwDownward * release,
                orientation = orientation,
                omega = rollingOmega(vx, vy, rollRadius) +
                    Quaternion.random(random).rotate(Vec3(0f, 0f, 1f)) * tumbleRate,
                groundHeight = groundHeightFor(die, orientation, arena.dieRadius),
                grip = throwGrip * (1f + (random.nextFloat() - 0.5f) * DIE_GRIP_JITTER)
            ).also { it.invInertia = inverseInertiaFor(die, arena.dieRadius) }
        }
    }

    /**
     * Inverse des moments d'inertie principaux à l'échelle du plateau. Le maillage les donne
     * pour un circumradius de 1 et une masse de 1 : l'inertie croît donc comme le carré du
     * rayon à l'écran.
     */
    private fun inverseInertiaFor(die: DisplayDie, dieRadius: Float): Vec3 {
        val scale = dieRadius * dieRadius
        val i = die.mesh.inertia
        return Vec3(1f / (i.x * scale), 1f / (i.y * scale), 1f / (i.z * scale))
    }

    /** Hauteur d'appui du dé dans son orientation courante, en pixels du plateau. */
    private fun groundHeight(s: DieSimState): Float =
        groundHeightFor(s.die, s.orientation, arena.dieRadius)

    /** Rayon de roulement du dé sur ce plateau : propriété du solide × taille à l'écran. */
    private fun rollRadius(s: DieSimState): Float =
        (arena.dieRadius * s.die.mesh.rollingRadius).coerceAtLeast(1e-3f)

    /**
     * Vitesse en dessous de laquelle le dé n'a plus de quoi franchir l'arête suivante : il
     * ne roule plus, il ne peut que racler la face sur laquelle il est posé. Le seuil est
     * purement géométrique — un d4, qui doit hisser son centre d'un quart de rayon pour
     * basculer, s'échoue bien plus tôt qu'un d20 qui trotte encore sur presque rien
     * ([PolyhedronMesh.tumbleBarrier]).
     *
     * C'est une **lecture** de l'état du dé, pas un mécanisme : rien dans la mécanique ne
     * change à ce seuil. Il sert à dire quand un dé a l'air posé.
     */
    private fun tumbleSpeed(s: DieSimState): Float =
        sqrt(2f * gravity * s.die.mesh.tumbleBarrier * arena.dieRadius * TUMBLE_ENERGY_SHARE)

    /**
     * Vrai quand [s] n'a plus de quoi franchir l'arête suivante : il ne roule plus, il racle
     * la face sur laquelle il est posé (voir [tumbleSpeed]). C'est le moment où un dé **a
     * l'air posé** — avant, si à plat qu'il soit, il est encore en train de rouler.
     */
    fun isBeached(s: DieSimState): Boolean = s.speed < tumbleSpeed(s)

    /**
     * Adapte le lancer à un plateau redimensionné (rotation d'écran, fenêtre, clavier) :
     * positions, vitesses et cases de rangement sont mises à l'échelle. Les dés poursuivent
     * leur course au lieu de repartir en l'air.
     */
    fun resize(next: ArenaSpec) {
        val previous = arena
        if (next.width <= 1f || next.height <= 1f || next == previous) return
        val scaleX = next.width / previous.width
        val scaleY = next.height / previous.height
        // Les vitesses suivent la dimension qui règle le seuil d'arrêt, pour que le lancer
        // garde exactement la même allure relative sur le nouveau plateau.
        val speedScale = minOf(next.width, next.height) / minOf(previous.width, previous.height)
        val spinScale = if (next.dieRadius > 1e-3f) {
            speedScale * previous.dieRadius / next.dieRadius
        } else {
            speedScale
        }

        val heightScale = if (previous.dieRadius > 1e-3f) next.dieRadius / previous.dieRadius else 1f

        arena = next
        stopSpeed = STOP_SPEED_FRACTION * minOf(next.width, next.height)
        gravity = GRAVITY_FACTOR * minOf(next.width, next.height)
        restingApproach = gravity * FIXED_STEP * RESTING_STEPS
        impactReference = IMPACT_REFERENCE_FACTOR * minOf(next.width, next.height)
        settleCells = settleGrid(states.size)

        states.forEach { s ->
            // Un dé encore hors piste le reste : la mise à l'échelle le suit.
            s.x = clampX(s, s.x * scaleX)
            s.y = clampY(s, s.y * scaleY)
            // La hauteur suit la taille du dé : seule la part au-dessus du feutre s'étire.
            // Le sol, lui, se recalcule : il dépend de l'orientation autant que du rayon.
            val ground = groundHeight(s)
            s.z = ground + (s.z - s.groundHeight) * heightScale
            s.groundHeight = ground
            s.invInertia = inverseInertiaFor(s.die, next.dieRadius)
            s.fromX = clampX(s.fromX * scaleX)
            s.fromY = clampY(s.fromY * scaleY)
            s.vx *= speedScale
            s.vy *= speedScale
            s.vz *= speedScale
            s.omega = s.omega * spinScale
        }

        // Les dés déjà rangés — ou en route — visent des cases qui viennent de bouger.
        if (states.any { it.phase == DiePhase.GATHERING || it.phase == DiePhase.SETTLED }) {
            assignSettleCells()
            states.forEach { s ->
                if (s.phase == DiePhase.SETTLED) {
                    s.x = s.targetX
                    s.y = s.targetY
                }
            }
        }

        // La pose précédente décrivait l'ancien plateau : interpoler depuis elle ferait glisser
        // chaque dé de sa place d'avant à sa nouvelle place. L'affichage repart de l'état courant.
        states.forEach { s ->
            s.rememberPose()
            s.blendPose(1f)
        }
    }

    /**
     * Avance le lancer de [dtSeconds] — typiquement l'intervalle entre deux images.
     *
     * Le temps est mis en réserve et consommé par pas entiers de [FIXED_STEP] : la cadence
     * d'affichage ne décide plus de la physique, seulement de la fréquence à laquelle on
     * vient la regarder. Le reliquat, toujours inférieur à un pas, est reporté sur l'appel
     * suivant, si bien qu'aucun temps ne se perd en route.
     *
     * Une image anormalement longue est écrêtée à [MAX_FRAME_SECONDS] : mieux vaut un lancer
     * qui prend du retard sur l'horloge qu'une rafale de pas rattrapant une seconde de gel.
     *
     * Le reliquat sert aussi à l'affichage : la pose montrée est interpolée entre les deux
     * derniers pas au prorata de ce temps en réserve ([DieSimState.drawX] et ses voisines).
     */
    fun step(dtSeconds: Float) {
        if (isSettled || dtSeconds <= 0f) return
        pendingSeconds += dtSeconds.coerceAtMost(MAX_FRAME_SECONDS)
        while (pendingSeconds >= FIXED_STEP) {
            pendingSeconds -= FIXED_STEP
            states.forEach { it.rememberPose() }
            integrate(FIXED_STEP)
            if (isSettled) {
                pendingSeconds = 0f
                // Plus de pas à venir vers lequel interpoler : la pose finale est la vraie.
                states.forEach { it.blendPose(1f) }
                return
            }
        }
        val alpha = pendingSeconds / FIXED_STEP
        states.forEach { it.blendPose(alpha) }
    }

    private fun integrate(dtSeconds: Float) {
        elapsedSeconds += dtSeconds
        states.forEach { s ->
            when (s.phase) {
                DiePhase.ROLLING -> {
                    val rollRadius = rollRadius(s)
                    integrateRoll(s, dtSeconds)
                    // Un dé encore en l'air, ou qui culbute encore, n'est pas arrêté. Le
                    // figer dès que sa translation s'épuise le priverait de la dernière
                    // bascule, celle qui le couche à plat — et une collision peut annuler
                    // sa vitesse d'un coup, sans rien enlever à sa rotation.
                    // « Épuisé » : posé, et sans plus de quoi franchir l'arête suivante. Un dé
                    // qui roule encore n'entre jamais dans ce cas ; un dé bousculé par ses
                    // voisins, si — et c'est justement celui qu'il faut aider à se coucher.
                    val spent = s.isGrounded && isBeached(s)
                    val stilled = spent && s.speed < stopSpeed &&
                        s.omega.length < stopSpinFor(stopSpeed, rollRadius) && isLyingFlat(s)
                    if (spent && !stilled) nudgeOffBalance(s, dtSeconds, rollRadius)
                    if (elapsedSeconds > MAX_ROLL_SECONDS || stilled) comeToRest(s)
                }

                DiePhase.GATHERING -> advanceGathering(s, dtSeconds)
                DiePhase.RESTING, DiePhase.SETTLED -> Unit
            }
        }
        resolveDieCollisions(dtSeconds)
        if (states.all { it.phase == DiePhase.RESTING }) beginGathering()
    }

    private fun integrateRoll(s: DieSimState, dt: Float) {
        val r = arena.dieRadius
        // Écart au feutre **avant** le pas : [DieSimState.groundHeight] décrit encore
        // l'orientation d'où le dé part. Positif, le dé vole et le contact qui vient sera un
        // choc ; nul ou négatif, il appuie déjà et ce n'est que la suite de cet appui.
        val clearance = s.z - s.groundHeight
        val fromX = s.x
        val fromY = s.y
        val fromZ = s.z
        val fromVz = s.vz
        val fromQ = s.orientation
        val fromOmega = s.omega

        // 1. Vol libre. La pesanteur et l'inertie du solide, rien d'autre. La rotation est
        //    intégrée avant le contact : c'est elle qui décide de la hauteur d'appui, donc de
        //    l'enfoncement que le feutre aura à corriger.
        advanceFlight(s, dt)

        // 2. Contact avec le feutre, s'il y a lieu.
        var ground = refreshContact(s)
        var remaining = 0f
        // Vrai quand le pas a été coupé à l'instant du contact : le dé y est **exactement** à
        // fleur de feutre, et `s.z < ground` ne suffirait donc pas à déclencher la résolution.
        var hinged = false
        if (s.z < ground && clearance > 0f) {
            // **Le dé volait et vient de traverser le feutre en cours de pas.** Le pas est
            // alors rejoué en deux, la charnière posée à l'instant du contact : le dé y arrive
            // à fleur de feutre, sans enfoncement à corriger, et le reste du pas se joue après
            // l'impulsion.
            //
            // Sans ce découpage, l'enfoncement croît avec la vitesse d'arrivée et la remontée
            // qui le corrige **offre** au dé l'énergie potentielle de cette remontée : à
            // 2 000 px/s d'approche et 1/240 s de pas, huit pixels d'enfoncement rendent 1,5 %
            // de l'énergie du lancer en une seule image. Ce n'est pas un défaut de convergence
            // du solveur — multiplier ses passes n'y change rien — mais du téléport lui-même.
            val fraction = (clearance / (clearance + (ground - s.z))).coerceIn(0f, 1f)
            s.x = fromX
            s.y = fromY
            s.z = fromZ
            s.vz = fromVz
            s.orientation = fromQ
            s.omega = fromOmega
            advanceFlight(s, dt * fraction)
            ground = refreshContact(s)
            // L'instant du contact est interpolé : le dé peut retomber un cheveu au-dessus du
            // feutre. On le pose dessus — descendre ne peut que lui **prendre** de l'énergie.
            s.z = minOf(s.z, ground)
            remaining = dt * (1f - fraction)
            hinged = true
        }
        s.groundHeight = ground
        if (hinged || s.z < ground) {
            // Le dé est remonté sur le feutre **sans qu'on lui invente de vitesse** : une
            // correction de pénétration qui pousse fait repartir l'auto-catapulte.
            //
            // Et on ne remonte que l'excès : un dé qui pivote sur son arête voit son sol monter
            // sous lui à chaque pas, et le recoller au micron près lui offrirait l'énergie
            // potentielle de cette montée sans la lui facturer. Sur un dé qui se dandine en
            // fin de course, cette monnaie de singe suffisait à entretenir le ballant
            // indéfiniment — le dé roulait jusqu'au garde-fou des 8 secondes.
            val slop = PENETRATION_SLOP * arena.dieRadius
            s.z += maxOf(ground - s.z - slop, 0f)
            if (s.hasEnteredTray) {
                solveFeltContact(s)
            } else {
                // Hors piste, le dé survole le rebord : il **tombe** comme n'importe quel corps
                // lâché, mais n'échange rien avec un feutre qui n'est pas là — ni frottement,
                // ni impulsion, et il ne percute pas ses voisins ([resolveDieCollisions]).
                // Le laisser interagir là épuisait les jets les plus mous avant qu'ils n'aient
                // atteint le plateau ; le faire voler à plat, à l'inverse, reportait toute sa
                // chute à l'instant de l'entrée — d'où un plongeon brutal, très visible parce
                // que la perspective déplace d'autant plus un dé à l'écran qu'il est haut.
                s.vz = 0f
            }
        }
        if (remaining > 0f) {
            // Fin du pas, une fois le choc encaissé. Le contact vient d'annuler la vitesse
            // d'approche : ce vol ne peut plus replonger le dé que de ce que la pesanteur lui
            // reprend en une fraction de pas, soit un vingtième de pixel — le pas suivant le
            // traitera comme l'appui posé qu'il est devenu.
            advanceFlight(s, remaining)
            s.groundHeight = refreshContact(s)
        }

        // 3. Murs. L'entrée dans la piste se juge encore sur le rayon complet, qui borne le dé
        //    quelle que soit son orientation : une fois dedans, aucun sommet n'est donc au-delà
        //    d'une paroi. C'est ensuite sa forme réelle qui la touche ([pressAgainstWall]).
        val minX = arena.wallInset + r
        val maxX = arena.width - arena.wallInset - r
        val minY = arena.topInset + r
        val maxY = (arena.height - arena.wallInset - r).coerceAtLeast(minY)
        // Le dé est lâché hors piste et y entre en volant : tant qu'il n'a pas franchi le
        // bord, les murs n'existent pas pour lui — sinon il rebondirait dessus par
        // l'extérieur au lieu d'entrer. L'entrée est sans retour.
        //
        // Il reste en revanche tenu dans la **section** du plateau, sur l'axe transverse au
        // bord qu'il va franchir : sans quoi une bousculade entre voisins peut le déporter
        // au-delà d'un mur latéral, et il longe alors la paroi par l'extérieur sans jamais
        // entrer — il court jusqu'au garde-fou de durée et se fige de travers, hors cadre.
        if (!s.hasEnteredTray) {
            if (throwThroughSide) s.y = s.y.coerceIn(minY, maxY) else s.x = s.x.coerceIn(minX, maxX)
        }
        if (!s.hasEnteredTray && s.x in minX..maxX && s.y in minY..maxY) s.hasEnteredTray = true
        if (s.hasEnteredTray) {
            pressAgainstWall(s, LEFT_WALL, arena.wallInset)
            pressAgainstWall(s, RIGHT_WALL, -(arena.width - arena.wallInset))
            pressAgainstWall(s, BACK_WALL, arena.topInset)
            pressAgainstWall(s, FRONT_WALL, -(arena.height - arena.wallInset))
        }
    }

    /**
     * Contact avec une paroi verticale, **par la forme réelle du dé**. [inward] est la normale de
     * la paroi tournée vers l'intérieur du plateau et [offset] sa position le long de celle-ci :
     * un point est dans la piste quand `p · inward ≥ offset`.
     *
     * Un mur est un plan, comme le feutre : il est donc traité comme lui ([pressAgainstPlane]),
     * par les sommets qui le touchent vraiment. Le traiter en sphère du rayon circonscrit laissait
     * entre la paroi et un cube posé à plat contre elle un vide de 0,42 rayon — le dé rebondissait
     * sur un coussin invisible — et frappait toujours à hauteur du centre : un dé qui heurte le mur
     * par un coin bas n'en repartait jamais avec l'effet qu'il devrait en prendre.
     */
    private fun pressAgainstWall(s: DieSimState, inward: Vec3, offset: Float) {
        val r = arena.dieRadius
        val depth = s.x * inward.x + s.y * inward.y - offset
        // Même une pointe tendue vers la paroi ne l'atteint pas : rien à résoudre.
        if (depth >= r) return
        s.die.mesh.support(s.orientation, inward * -1f, wallContact)
        val gap = depth - wallContact.height * r
        if (gap >= 0f) return
        // Remis à fleur de paroi, sans vitesse inventée. Contrairement au feutre, aucune énergie
        // potentielle n'est en jeu à l'horizontale : la remontée ne lui offre rien.
        s.x -= inward.x * gap
        s.y -= inward.y * gap
        if (isShock(s, wallContact, inward)) {
            pressAgainstPlane(
                s, wallContact, inward, WALL_RESTITUTION, WALL_FRICTION,
                DiceImpactKind.WALL, WALL_IMPACT_GAIN
            )
        } else {
            // **Un simple appui ne peut pas étayer le dé.** Résolu par les sommets, il retenait
            // un dé penché de quelques degrés contre la paroi : chaque pas annulait la bascule
            // qui l'aurait couché, et le lancer courait jusqu'au garde-fou des onze secondes
            // (un sur quatre-vingts). Un vrai dé, lui, glisse du pied et se couche. On ne retire
            // donc que la vitesse du centre vers la paroi, sans couple : la bascule enfonce
            // alors un sommet, que la remise à fleur du pas suivant éloigne du mur.
            val into = s.vx * inward.x + s.vy * inward.y
            if (into < 0f) {
                s.vx -= inward.x * into
                s.vy -= inward.y * into
            }
        }
    }

    /**
     * Vrai quand un sommet de [support] arrive sur le plan de normale [normal] plus vite que ce
     * que la pesanteur donne en quelques pas : un choc, et non un appui ([restingApproach]).
     */
    private fun isShock(s: DieSimState, support: SupportContact, normal: Vec3): Boolean {
        for (index in 0 until support.count) {
            if (-(pointVelocity(s, armOf(s, support, index)) dot normal) > restingApproach) return true
        }
        return false
    }

    /**
     * Vol libre pendant [dt] : la pesanteur, la rotation propre du solide, le déplacement.
     * Rien d'autre — aucun contact n'y est vu. C'est la brique dont [integrateRoll] fait
     * ses deux sous-pas quand le dé touche le feutre en cours de route.
     */
    private fun advanceFlight(s: DieSimState, dt: Float) {
        s.vz -= gravity * dt
        advanceFreeSpin(s, dt)
        s.x += s.vx * dt
        s.y += s.vy * dt
        s.z += s.vz * dt
    }

    /**
     * Remplit [contact] avec l'appui du dé dans son orientation courante, et renvoie la hauteur
     * de son centre en pixels du plateau.
     */
    private fun refreshContact(s: DieSimState): Float {
        s.die.mesh.support(s.orientation, contact)
        return contact.height * arena.dieRadius
    }

    /**
     * Vitesse du point du dé situé en [arm] (bras de levier depuis le centre) : `v + ω × arm`.
     * C'est elle que voit la surface touchée, et jamais celle du dé — un dé qui roule sans
     * glisser a un point de contact **immobile**, et c'est pour ça qu'il roule loin.
     */
    private fun pointVelocity(s: DieSimState, arm: Vec3): Vec3 =
        Vec3(s.vx, s.vy, s.vz) + (s.omega cross arm)

    /**
     * Changement de rotation produit par une impulsion angulaire, `I⁻¹·τ`. Le tenseur d'inertie
     * est diagonal dans le repère du dé : on y ramène le vecteur, on divise, on revient.
     */
    private fun angularResponse(s: DieSimState, torque: Vec3): Vec3 {
        val i = s.invInertia
        val body = s.orientation.inverseRotate(torque)
        return s.orientation.rotate(Vec3(body.x * i.x, body.y * i.y, body.z * i.z))
    }

    /**
     * Masse effective du dé en [arm] dans la direction [n] : de combien la vitesse de ce point
     * change pour une impulsion unité. Elle vaut 1 — la masse — pour une poussée dans l'axe du
     * centre, et davantage dès qu'un bras de levier met l'inertie de rotation dans la balance.
     */
    private fun effectiveMass(s: DieSimState, arm: Vec3, n: Vec3): Float =
        1f + ((angularResponse(s, arm cross n) cross arm) dot n)

    /** Bras de levier du sommet d'indice [index] de l'appui [support], dans le repère du plateau. */
    private fun armOf(s: DieSimState, support: SupportContact, index: Int): Vec3 =
        s.orientation.rotate(s.die.mesh.vertices[support.vertices[index]]) * arena.dieRadius

    /** Applique une impulsion au point [arm] : la masse vaut 1, le bras fait le reste. */
    private fun applyImpulse(s: DieSimState, arm: Vec3, impulse: Vec3) {
        s.vx += impulse.x
        s.vy += impulse.y
        s.vz += impulse.z
        s.omega = s.omega + angularResponse(s, arm cross impulse)
    }

    /**
     * **Le feutre pousse le dé là où il le touche.** Chaque sommet d'appui reçoit une impulsion
     * normale qui annule son enfoncement, puis une impulsion de frottement de Coulomb qui
     * s'oppose à son patinage, bornée par la charge qu'il porte ([FELT_FRICTION]).
     *
     * De ce seul mécanisme sortent la culbute (un appui décalé fait un couple), le pivot du dé
     * autour de son arête (le frottement interdit au point d'appui de patiner, donc le centre
     * décrit l'arc), le prix de la bascule, le décollage et le couchage final.
     *
     * Les appuis sont résolus **à tour de rôle sur plusieurs passes, en cumulant** l'impulsion
     * de chacun. Le cumul est ce qui compte : borner à zéro la charge **totale** d'un appui, et
     * non chaque incrément, permet à une passe de *retirer* ce qu'une autre a mis en trop —
     * donc à un appui de se décharger complètement. Sans quoi trois sommets suffisent toujours
     * à retenir le dé, et un barillet posé sur une coiffe y reste indéfiniment alors que son
     * centre tombe hors du triangle : il devrait basculer sur son flanc.
     */
    private fun solveFeltContact(s: DieSimState) {
        val load = pressAgainstPlane(
            s, contact, UP, BOUNCE_RESTITUTION, FELT_FRICTION, DiceImpactKind.FELT, FELT_IMPACT_GAIN
        )
        if (load > 0f) resistRolling(s, load)
        // Le feutre n'est pas un tremplin : au-delà d'une certaine violence il cède et absorbe
        // l'excès au lieu de le renvoyer. Garde-fou visuel, pas un ressort de la mécanique.
        val maxVault = sqrt(2f * gravity * MAX_HOP_RADII * arena.dieRadius)
        if (s.vz > maxVault) s.vz = maxVault
    }

    /**
     * Résolution d'un appui sur un plan de normale [normal] — le feutre ou une paroi — par les
     * sommets de [support] : impulsions normales cumulées, rebond au seul sommet qui frappe le
     * premier, puis frottement de Coulomb borné par la charge de chaque sommet. Voir
     * [solveFeltContact] pour le pourquoi de chacun de ces choix. Renvoie la charge totale.
     */
    private fun pressAgainstPlane(
        s: DieSimState,
        support: SupportContact,
        normal: Vec3,
        restitution: Float,
        friction: Float,
        kind: DiceImpactKind,
        impactGain: Float
    ): Float {
        val points = support.count
        var deepest = 0
        var hardest = 0f
        for (index in 0 until points) {
            // Vitesse d'approche du sommet **avant** toute impulsion : c'est elle qui dit si le
            // dé tombe sur le plan ou s'y appuie, et elle sert de consigne à toutes les
            // passes. La relire à chaque passe ferait rebondir le dé sur son propre rebond.
            val approach = pointVelocity(s, armOf(s, support, index)) dot normal
            impulses[index] = 0f
            targets[index] = 0f
            if (approach < hardest) {
                hardest = approach
                deepest = index
            }
        }
        // Le rebond ne se joue qu'**au sommet qui frappe le premier**. Un dé qui se pose à plat
        // touche par trois ou quatre sommets à la fois, et faire rebondir chacun d'eux revient à
        // le renvoyer plusieurs fois du même choc : il repartait avec un quart d'énergie de
        // plus qu'il n'en avait.
        if (points > 0 && -hardest > restingApproach) {
            targets[deepest] = -restitution * hardest
            // Ce qui dépasse le seuil de repos, et non la vitesse d'approche brute : un dé qui
            // finit de se coucher frôle ce seuil à chaque pas, et compter sa vitesse entière lui
            // ferait bourdonner la main pendant toute sa fin de course.
            recordImpact(kind, -hardest - restingApproach, impactGain)
        }
        repeat(CONTACT_ITERATIONS) {
            for (index in 0 until points) {
                val arm = armOf(s, support, index)
                val approach = pointVelocity(s, arm) dot normal
                val wanted = (targets[index] - approach) / effectiveMass(s, arm, normal)
                val total = (impulses[index] + wanted).coerceAtLeast(0f)
                val change = total - impulses[index]
                impulses[index] = total
                if (change != 0f) applyImpulse(s, arm, normal * change)
            }
        }
        // Frottement, une fois les charges connues : un sommet à peine appuyé ne retient
        // presque rien, un dé qui claque sur le plan y accroche franchement.
        var load = 0f
        for (index in 0 until points) {
            load += impulses[index]
            rubAgainst(s, armOf(s, support, index), normal, impulses[index], friction)
        }
        return load
    }

    /**
     * Résistance au roulement et au pivotement. Le frottement de Coulomb, seul, n'arrête pas un
     * dé qui roule sans glisser : son point de contact ne patine pas, donc rien ne le freine et
     * il roulerait indéfiniment. C'est le feutre qui s'enfonce sous lui et lui oppose un couple.
     *
     * Le budget est proportionnel à la charge, comme tout frottement — un dé en l'air ne freine
     * pas — et [DieSimState.grip] en fait **le** levier de durée du lancer.
     */
    private fun resistRolling(s: DieSimState, load: Float) {
        val budget = load * arena.dieRadius * s.grip
        val tumble = Vec3(s.omega.x, s.omega.y, 0f)
        s.omega = brakeSpin(s, s.omega, tumble, budget * ROLL_RESISTANCE)
        val pivot = Vec3(0f, 0f, s.omega.z)
        s.omega = brakeSpin(s, s.omega, pivot, budget * PIVOT_RESISTANCE)
    }

    /**
     * Retire de [omega] ce que le couple [budget] peut effacer de [component], sans jamais
     * l'inverser : un frottement arrête, il ne fait pas repartir en sens contraire.
     *
     * Le freinage est écrit **le long de l'axe** et non comme une impulsion angulaire : sur un
     * solide dont les moments diffèrent — le barillet, le trapézoèdre — `I⁻¹·τ` n'est pas
     * colinéaire à `τ`, et un frein qui dévie ajoute de la rotation ailleurs au lieu d'en
     * retirer. Le dé y gagnait un ballant qu'il ne perdait plus.
     */
    private fun brakeSpin(s: DieSimState, omega: Vec3, component: Vec3, budget: Float): Vec3 {
        val rate = component.length
        if (rate < SPIN_EPSILON || budget <= 0f) return omega
        val axis = component * (1f / rate)
        return omega - axis * minOf(rate, budget / inertiaAbout(s, axis))
    }

    /** Moment d'inertie du dé autour d'un axe du plateau, à l'échelle du plateau. */
    private fun inertiaAbout(s: DieSimState, axis: Vec3): Float {
        val i = s.die.mesh.inertia
        val a = s.orientation.inverseRotate(axis)
        val r = arena.dieRadius
        return (i.x * a.x * a.x + i.y * a.y * a.y + i.z * a.z * a.z) * r * r
    }

    /**
     * Frottement tangentiel d'un contact ponctuel dont la charge [load] est déjà connue : le
     * patinage du point de contact est freiné dans la limite de [friction] fois cette charge.
     */
    private fun rubAgainst(s: DieSimState, arm: Vec3, normal: Vec3, load: Float, friction: Float) {
        val point = pointVelocity(s, arm)
        val slip = point - normal * (point dot normal)
        val patinage = slip.length
        if (patinage < SLIP_EPSILON) return
        val direction = slip * (1f / patinage)
        val grip = minOf(patinage / effectiveMass(s, arm, direction), friction * load)
        applyImpulse(s, arm, direction * -grip)
    }

    /**
     * Rotation libre du solide. Sans couple extérieur, c'est le **moment cinétique** qui se
     * conserve, pas la vitesse angulaire : un solide dont les moments principaux diffèrent voit
     * donc son axe de rotation dériver dans son propre repère à mesure qu'il tourne. C'est ce
     * qui fait se dandiner un dé cristal lancé en l'air ; sur un solide de Platon — isotrope —
     * l'inertie est la même dans toutes les directions et le dé tourne d'un bloc.
     */
    private fun advanceFreeSpin(s: DieSimState, dt: Float) {
        // Tous ces solides sont **de révolution autour d'un axe** (deux moments égaux, un
        // troisième à part — voir [PolyhedronMesh.inertia]), et la toupie libre a dans ce cas
        // une solution exacte : dans le repère du dé, la vitesse angulaire tourne autour de
        // l'axe de symétrie à la vitesse constante `ω₃·(I₃ − I₁)/I₁`, sans rien changer à sa
        // norme. On l'applique telle quelle, donc sans erreur d'intégration : ni l'énergie ni
        // le moment cinétique ne peuvent dériver.
        //
        // Écrire l'équation d'Euler en pas explicites, elle, faisait diverger un barillet lancé
        // vite — son terme gyroscopique est quadratique en ω — et conserver le seul moment
        // cinétique laissait l'énergie de rotation gonfler de 30 % en un pas.
        val i = s.die.mesh.inertia
        val body = s.orientation.inverseRotate(s.omega)
        val precession = body.z * (i.z - i.x) / i.x
        val spun = if (abs(precession) * dt > 1e-6f) {
            val angle = precession * dt
            val c = cos(angle)
            val sn = sin(angle)
            Vec3(body.x * c - body.y * sn, body.x * sn + body.y * c, body.z)
        } else {
            body
        }
        s.orientation = (spinRotation(s.omega, dt) * s.orientation).normalized()
        s.omega = s.orientation.rotate(spun)
    }

    /**
     * Face **à valeur** qui regarde le plus vers le feutre : celle sur laquelle le dé va se
     * coucher. Les faces sans chiffre sont écartées — les coiffes du d4 barillet n'existent que
     * pour l'empêcher de rester debout, et une coiffe à plat ne doit donc jamais compter comme
     * une assiette de repos ([isLyingFlat]). La mécanique, elle, ne connaît pas cette
     * distinction : le contact s'appuie sur toute l'enveloppe, et si le barillet ne tient pas
     * sur une coiffe, c'est parce que son centre tombe hors du triangle d'appui — la géométrie
     * du solide y pourvoit (voir `DiceMeshes.BARREL_HALF_LENGTH`).
     */
    private fun bottomFace(s: DieSimState): Face {
        val faces = s.die.mesh.valueFaces
        var bottom = faces[0]
        var lowest = Float.MAX_VALUE
        for (index in faces.indices) {
            val nz = s.orientation.rotatedZ(faces[index].normal)
            if (nz < lowest) {
                lowest = nz
                bottom = faces[index]
            }
        }
        return bottom
    }

    /**
     * Coup de pouce au dé en équilibre. Un polyèdre peut s'arrêter **posé sur une arête**, son
     * centre exactement à l'aplomb de celle-ci : l'équilibre est alors réel, et la mécanique du
     * contact n'a rigoureusement aucun couple à lui opposer. Un dé parfait sur un feutre parfait
     * y resterait indéfiniment — le barillet du d4 y tombait systématiquement, à quelques degrés
     * de sa coiffe, et roulait jusqu'au garde-fou des huit secondes.
     *
     * Ce n'est pas un mécanisme de redressement : c'est ce qui manque à un monde parfait pour
     * ressembler au vrai. Le feutre n'est pas plan, le dé n'est pas exactement symétrique, et
     * un équilibre sur arête est **instable** — il ne tient jamais. Le couple pousse donc vers
     * la face qui regarde déjà le plus vers le bas, s'annule dès qu'elle est à plat, et ne
     * s'applique qu'à un dé déjà immobile : pendant la course, le contact fait tout le travail.
     */
    private fun nudgeOffBalance(s: DieSimState, dt: Float, rollRadius: Float) {
        val n = s.orientation.rotate(bottomFace(s).normal)
        // n × (0,0,−1) : axe dans le plan du plateau, de norme sin(inclinaison). C'est la
        // direction qui abaisse le centre, celle vers laquelle un dé penché veut tomber.
        var axis = Vec3(-n.y, n.x, 0f)
        // [contact] décrit encore l'appui de ce dé : la boucle d'intégration enchaîne les deux
        // sur le même dé.
        if (contact.count == 2) {
            // Posé sur une arête, le dé ne peut tomber qu'**autour** d'elle. Toute autre
            // rotation enfoncerait un appui dans le feutre, et le contact l'annulerait au pas
            // suivant : le dé se dandinait sur place pendant huit secondes.
            val first = armOf(s, contact, 0)
            val second = armOf(s, contact, 1)
            val edge = second - first
            val length = edge.length
            if (length > SLIP_EPSILON) {
                val direction = edge * (1f / length)
                // Reste à choisir le sens : celui qui **abaisse le centre**, puisque c'est de
                // sa hauteur que le dé est prisonnier. Le pendule ne tranche que si les deux
                // sens se valent, c'est-à-dire quand le centre est pile à l'aplomb de l'arête.
                val lever = (first + second) * -0.5f
                val descent = (direction cross lever).z
                val sense = when {
                    descent > SPIN_EPSILON -> -1f
                    descent < -SPIN_EPSILON -> 1f
                    else -> if ((axis dot direction) >= 0f) 1f else -1f
                }
                axis = direction * (sense * axis.length)
            }
        }
        s.omega = s.omega + axis * (BALANCE_NUDGE * gravity / rollRadius * dt)
    }

    /**
     * Vrai quand le dé repose bien à plat sur une face **à valeur**. Un dé encore dressé sur
     * une arête n'est pas au repos : son centre est hors de son polygone d'appui, donc le
     * contact le fait basculer. C'est ce qui garantit qu'un dé ne se fige jamais penché, même
     * si une collision lui coupe sa vitesse en pleine bascule.
     */
    private fun isLyingFlat(s: DieSimState): Boolean =
        s.orientation.rotatedZ(bottomFace(s).normal) <= -FLAT_COSINE

    /**
     * Le dé n'a plus d'élan : il s'arrête là où il en est. Il y arrive **déjà couché à
     * plat**, le contact l'ayant fait basculer sur sa face pendant la fin de course — c'est
     * précisément la condition d'arrêt, puisqu'un dé encore penché garde une vitesse de
     * rotation. Le résidu d'inclinaison est de l'ordre du degré ; le
     * regroupement l'absorbe, sans à-coup visible.
     */
    private fun comeToRest(s: DieSimState) {
        s.vx = 0f
        s.vy = 0f
        s.vz = 0f
        s.groundHeight = groundHeight(s)
        s.z = s.groundHeight
        s.omega = Vec3.ZERO
        s.stoppedRollingAt = elapsedSeconds
        s.landedFace = landedFace(s.die, s.orientation)
        s.phase = DiePhase.RESTING
    }

    /**
     * Tous les dés ont fini de rouler : ils rejoignent ensemble la zone de lecture. Les
     * cases sont réparties au plus proche (affectation gloutonne) pour limiter les
     * croisements. Le dé est déjà couché sur sa face : il ne reste que quelques degrés
     * d'assiette à rattraper, et le déplacement les absorbe.
     */
    private fun beginGathering() {
        assignSettleCells()
        states.forEach { s ->
            s.fromX = s.x
            s.fromY = s.y
            s.fromQ = s.orientation
            s.targetOrientation = s.landedFace
                ?.let { settleOrientationOnto(it, s.orientation) }
                ?: s.orientation
            s.gatherElapsed = 0f
            s.phase = DiePhase.GATHERING
        }
    }

    /** Affectation gloutonne dé → case au plus proche, pour limiter les croisements. */
    private fun assignSettleCells() {
        val freeCells = settleCells.toMutableList()
        val pending = states.toMutableList()
        while (pending.isNotEmpty() && freeCells.isNotEmpty()) {
            var bestDie = pending.first()
            var bestCell = freeCells.first()
            var bestDist = Float.MAX_VALUE
            pending.forEach { die ->
                freeCells.forEach { cell ->
                    val dx = die.x - cell.first
                    val dy = die.y - cell.second
                    val d = dx * dx + dy * dy
                    if (d < bestDist) { bestDist = d; bestDie = die; bestCell = cell }
                }
            }
            bestDie.targetX = bestCell.first
            bestDie.targetY = bestCell.second
            pending.remove(bestDie)
            freeCells.remove(bestCell)
        }
    }

    private fun advanceGathering(s: DieSimState, dt: Float) {
        s.gatherElapsed += dt
        val progress = (s.gatherElapsed / gatherDuration).coerceAtMost(1f)
        val eased = 1f - (1f - progress).let { it * it * it }
        s.x = s.fromX + (s.targetX - s.fromX) * eased
        s.y = s.fromY + (s.targetY - s.fromY) * eased
        // Le redressement suit sa propre courbe, adoucie aux deux bouts : partager
        // l'amorti du déplacement le rendrait brusque au démarrage, là où le dé vient
        // justement de passer un moment parfaitement immobile.
        val turned = progress * progress * (3f - 2f * progress)
        s.orientation = Quaternion.slerp(s.fromQ, s.targetOrientation, turned)
        if (progress >= 1f) {
            s.orientation = s.targetOrientation
            s.phase = DiePhase.SETTLED
        }
        // Le dé s'assoit sur sa face en chemin : la hauteur d'appui suit le redressement,
        // sans quoi le dernier degré d'inclinaison le laisserait flotter d'un cheveu.
        s.groundHeight = groundHeight(s)
        s.z = s.groundHeight
    }

    /**
     * Séparation des dés qui se chevauchent pendant le roulement. Un dé arrêté ne bouge
     * plus : c'est celui qui roule encore qui est repoussé. Une fois le regroupement
     * lancé, plus aucun dé n'est mobile et les cases visées sont déjà disjointes.
     */
    private fun resolveDieCollisions(dt: Float) {
        val r = arena.dieRadius
        // Deux sphères de rayon circonscrit enveloppent les dés quelle que soit leur orientation :
        // au-delà, ils ne peuvent pas se toucher, et la forme réelle n'a pas à être consultée.
        val bound = 2f * r
        // Les deux bornes de résorption sont des **taux**, pas des parts par pas : le pas
        // d'intégration étant désormais plus fin que l'image, un pourcentage par pas
        // rendrait la séparation quatre fois plus vive à l'écran et ramènerait le téléport
        // que ces bornes existent justement pour empêcher.
        val relaxation = 1f - exp(-SEPARATION_RELAXATION_RATE * dt)
        val maxSeparation = MAX_SEPARATION_SPEED * arena.dieRadius * dt
        for (i in states.indices) for (j in i + 1 until states.size) {
            val a = states[i]
            val b = states[j]
            // Un dé encore en vol au-dessus du rebord et un dé déjà lancé sur le feutre ne
            // sont pas au même étage : le premier survole le second. Les faire se pousser
            // créait un embouteillage à l'entrée — les arrivants butaient sur ceux qui
            // avaient déjà ralenti, et les derniers d'une grosse poignée n'entraient jamais.
            if (a.hasEnteredTray != b.hasEnteredTray) continue
            val dx = b.x - a.x
            val dy = b.y - a.y
            val dist = sqrt(dx * dx + dy * dy)
            if (dist >= bound || dist < 1e-4f) continue
            val nx = dx / dist
            val ny = dy / dist
            // Distance de contact le long de la ligne des centres : l'étendue de chaque dé vers
            // l'autre, par sa forme réelle. Deux cubes face à face se touchent à 1,15 rayon, pas
            // à 2 — en sphères, ils rebondissaient l'un sur l'autre à 0,85 rayon de distance.
            // Si leurs étendues ne se recouvrent pas sur cette ligne, les solides sont disjoints :
            // un vrai chevauchement n'échappe donc jamais au test. L'inverse n'est pas vrai —
            // deux dés de biais peuvent être séparés selon un autre axe —, et le contact se juge
            // alors un peu tôt, sans jamais aller au-delà des sphères d'avant.
            val minDist = (
                a.die.mesh.reach(a.orientation, Vec3(nx, ny, 0f)) +
                    b.die.mesh.reach(b.orientation, Vec3(-nx, -ny, 0f))
                ) * r
            if (dist >= minDist) continue
            // Le chevauchement se résorbe **progressivement**, et jamais de plus d'un pas
            // borné par image. Le corriger d'un coup téléportait les dés — jusqu'à un rayon
            // en une image, soit une pointe de vitesse apparente de plusieurs milliers de
            // pixels par seconde, très visible juste après le lancer.
            val overlap = minOf((minDist - dist) * relaxation, maxSeparation)
            val aMovable = a.phase == DiePhase.ROLLING
            val bMovable = b.phase == DiePhase.ROLLING
            // Deux dés encore en vol au-dessus du rebord se dégagent l'un de l'autre, mais ne
            // se percutent pas : une impulsion là dévierait le cap d'un dé qui, rien ne le
            // freinant hors piste, filerait sans fin jusqu'au garde-fou de durée. Les écarter
            // sans toucher à leur vitesse leur évite d'arriver empilés sur le feutre.
            val inPlay = a.hasEnteredTray && b.hasEnteredTray
            when {
                aMovable && bMovable -> {
                    // Clampé comme les autres branches : deux dés qui se percutent contre
                    // un mur ne doivent pas être poussés hors de l'arène.
                    a.x = clampX(a, a.x - nx * overlap / 2f); a.y = clampY(a, a.y - ny * overlap / 2f)
                    b.x = clampX(b, b.x + nx * overlap / 2f); b.y = clampY(b, b.y + ny * overlap / 2f)
                    if (inPlay) knock(a, b, Vec3(-nx, -ny, 0f))
                }

                aMovable -> {
                    a.x = clampX(a, a.x - nx * overlap); a.y = clampY(a, a.y - ny * overlap)
                    if (inPlay) knock(a, null, Vec3(-nx, -ny, 0f))
                }

                bMovable -> {
                    b.x = clampX(b, b.x + nx * overlap); b.y = clampY(b, b.y + ny * overlap)
                    if (inPlay) knock(b, null, Vec3(nx, ny, 0f))
                }
            }
        }
    }

    /**
     * Choc entre deux dés, ou d'un dé contre un dé déjà arrêté ([other] nul). L'impulsion est
     * appliquée au point où ils se touchent et non à leurs centres : un dé percuté de biais
     * repart **en tournant**, comme sur une vraie table.
     *
     * [away] est la direction dans laquelle [s] doit être repoussé. Chaque dé est touché par le
     * côté qu'il tend vers l'autre ([contactArm]) : la pointe, l'arête ou la face qui dépasse.
     * Un coin qui accroche loin du centre, ou en hauteur, fait donc partir le dé en tournant
     * bien plus qu'une face qui frappe à plat.
     */
    private fun knock(s: DieSimState, other: DieSimState?, away: Vec3) {
        var arm = contactArm(s, away * -1f)
        var otherArm = other?.let { contactArm(it, away) } ?: Vec3.ZERO
        var approach = closingSpeed(s, arm, other, otherArm, away)
        if (-approach <= restingApproach) {
            // Simple appui, et non choc : poussé au centre, sans couple. Par son point réel, un
            // dé penché s'étayait sur son voisin immobile et y restait jusqu'au garde-fou de
            // durée — voir [pressAgainstWall], où le même appui retenait un dé contre la paroi.
            arm = Vec3.ZERO
            otherArm = Vec3.ZERO
            approach = closingSpeed(s, arm, other, otherArm, away)
        }
        if (approach >= 0f) return
        val resistance = effectiveMass(s, arm, away) +
            (other?.let { effectiveMass(it, otherArm, away) } ?: 0f)
        val impulse = -(1f + DIE_RESTITUTION) * approach / resistance
        // Vitesse de rapprochement des deux dés, pour la même raison qu'en [pressAgainstPlane] :
        // les trois types de choc doivent se mesurer à la même aune pour se comparer entre eux.
        recordImpact(DiceImpactKind.DIE, -approach, DIE_IMPACT_GAIN)
        applyImpulse(s, arm, away * impulse)
        rubAgainst(s, arm, away, impulse, DIE_FRICTION)
        if (other != null) {
            val back = away * -1f
            applyImpulse(other, otherArm, back * impulse)
            rubAgainst(other, otherArm, back, impulse, DIE_FRICTION)
        }
    }

    /** Vitesse à laquelle [s] s'éloigne de [other] (ou d'un obstacle immobile) le long de [away]. */
    private fun closingSpeed(
        s: DieSimState,
        arm: Vec3,
        other: DieSimState?,
        otherArm: Vec3,
        away: Vec3
    ): Float = if (other == null) {
        pointVelocity(s, arm) dot away
    } else {
        (pointVelocity(s, arm) - pointVelocity(other, otherArm)) dot away
    }

    /**
     * Point par lequel [s] touche ce qui se trouve dans la direction [outward] : le centre des
     * sommets qui dépassent le plus de ce côté — une pointe, le milieu d'une arête ou le centre
     * d'une face —, en bras de levier depuis le centre du dé.
     */
    private fun contactArm(s: DieSimState, outward: Vec3): Vec3 {
        s.die.mesh.support(s.orientation, outward, probe)
        var sum = Vec3.ZERO
        for (index in 0 until probe.count) sum += armOf(s, probe, index)
        return if (probe.count == 0) sum else sum * (1f / probe.count)
    }

    private fun settleGrid(count: Int): List<Pair<Float, Float>> {
        val cols = gridColumns(count)
        val rows = ceil(count.toFloat() / cols).toInt()
        val r = arena.dieRadius
        val usableW = arena.width - 2f * (arena.wallInset + r)
        // Bande de lecture : la contrainte la plus forte l'emporte, elles ne s'additionnent
        // pas. Le filigrane pousse déjà la bande bien sous le mur du fond ; y rajouter le
        // retrait du mur la réduirait à une fente.
        val bandTop = maxOf(arena.height * arena.settleTopFraction, arena.topInset) + r
        val bandBottom =
            minOf(arena.height * arena.settleBottomFraction, arena.height - arena.wallInset) - r
        val bandHeight = (bandBottom - bandTop).coerceAtLeast(0f)
        var pitchX = SETTLE_PITCH_FACTOR * r
        if (cols > 1 && pitchX * (cols - 1) > usableW) pitchX = usableW / (cols - 1)
        var pitchY = SETTLE_PITCH_FACTOR * r
        if (rows > 1 && pitchY * (rows - 1) > bandHeight) pitchY = bandHeight / (rows - 1)
        val centerX = arena.width / 2f
        val centerY = bandTop + bandHeight / 2f
        return (0 until count).map { index ->
            val col = index % cols
            val row = index / cols
            val x = centerX + (col - (cols - 1) / 2f) * pitchX
            val y = centerY + (row - (rows - 1) / 2f) * pitchY
            clampX(x) to clampY(y)
        }
    }

    private fun clampX(x: Float): Float {
        val r = arena.dieRadius
        val min = arena.wallInset + r
        val max = (arena.width - arena.wallInset - r).coerceAtLeast(min)
        return x.coerceIn(min, max)
    }

    private fun clampY(y: Float): Float {
        val r = arena.dieRadius
        val min = arena.topInset + r
        val max = (arena.height - arena.wallInset - r).coerceAtLeast(min)
        return y.coerceIn(min, max)
    }

    /**
     * Bornages d'un dé dans la piste, par **son étendue réelle** vers chaque paroi : un cube posé
     * à plat contre un mur doit pouvoir y rester collé, et le borner au rayon circonscrit le
     * décollerait d'un bond à la première bousculade.
     *
     * Sans effet tant que le dé n'a pas franchi le bord : un dé encore hors piste doit pouvoir
     * être bousculé par ses voisins sans être aspiré dans le plateau.
     */
    private fun clampX(s: DieSimState, x: Float): Float {
        if (!s.hasEnteredTray) return x
        val min = arena.wallInset + reachTowardWall(s, LEFT_WALL)
        val max = (arena.width - arena.wallInset - reachTowardWall(s, RIGHT_WALL)).coerceAtLeast(min)
        return x.coerceIn(min, max)
    }

    private fun clampY(s: DieSimState, y: Float): Float {
        if (!s.hasEnteredTray) return y
        val min = arena.topInset + reachTowardWall(s, BACK_WALL)
        val max = (arena.height - arena.wallInset - reachTowardWall(s, FRONT_WALL)).coerceAtLeast(min)
        return y.coerceIn(min, max)
    }

    /** Étendue de [s], en pixels, vers la paroi de normale intérieure [inward]. */
    private fun reachTowardWall(s: DieSimState, inward: Vec3): Float =
        s.die.mesh.reach(s.orientation, inward * -1f) * arena.dieRadius

    companion object {
        /** Regroupement une fois tous les dés arrêtés (le guidage a déjà aligné les faces). */
        const val GATHER_DURATION_SECONDS = 0.5f

        /**
         * Garde-fou : au-delà, les dés se posent quoi qu'il arrive — au risque de figer un dé
         * qui n'a pas fini de se coucher, et que le regroupement redressera alors visiblement.
         * Il doit donc rester **au-delà du plus long lancer honnête** : une poignée de dix dés
         * ronds dans un petit plateau se bouscule longtemps, et sur 3 360 lancers mesurés c'est
         * à onze secondes que le dernier finit. À huit, on coupait cinq lancers parfaitement
         * normaux en pleine bascule.
         */
        const val MAX_ROLL_SECONDS = 11f

        /**
         * Pas d'intégration, fixe et indépendant de l'affichage (voir [step]). À 1/240 s, la
         * rotation d'un dé lancé à pleine vitesse reste sous 10° par pas — de quoi
         * échantillonner honnêtement la fonction d'appui, dont chaque bosse d'arête ne dure
         * qu'une fraction de culbute. La simulation étant en O(dés × sommets), quadrupler le
         * nombre de pas reste négligeable devant le rendu.
         */
        const val FIXED_STEP = 1f / 240f

        /**
         * Écrêtage d'une image anormalement longue (gel, reprise d'arrière-plan) : au-delà,
         * le temps est perdu plutôt que rattrapé d'une rafale de pas.
         */
        private const val MAX_FRAME_SECONDS = 0.05f

        /** Pas et garde-fou du déroulement accéléré (voir [fastForward]). */
        private const val FAST_FORWARD_STEP = 1f / 60f
        private const val FAST_FORWARD_MAX_STEPS = 2000

        /**
         * Vitesse du jet, en fraction de la plus petite dimension de l'arène par seconde.
         * Tirée **une fois pour toute la poignée** : c'est le geste qui varie d'un lancer à
         * l'autre, pas les dés entre eux. La borne haute est celle d'un dé qui traverse le
         * plateau en un peu moins d'une seconde ; au-delà, le culbutage n'est plus
         * échantillonnable à 60 im/s et le dé stroboscope au lieu de rouler.
         */
        private const val MIN_SPEED_FACTOR = 1.0f
        private const val MAX_SPEED_FACTOR = 2.2f

        /** Dispersion du cap d'un dé à l'autre au sein d'une même poignée (±11°). */
        private const val DIE_HEADING_SPREAD = 0.38f

        /** Dispersion de vitesse au sein de la poignée, en fraction de la vitesse du jet (±11 %). */
        private const val DIE_SPEED_JITTER = 0.22f

        /** Dispersion de la hauteur de lâcher au sein de la poignée (±10 %). */
        private const val DIE_DROP_JITTER = 0.20f

        /** Écartement des dés de front à la sortie de la main, en rayons. */
        private const val THROW_ABREAST_PITCH = 2.2f

        /** Écartement des rangs de la poignée le long de l'axe du jet, en rayons. */
        private const val THROW_ROW_PITCH = 2.2f

        /** Décalage aléatoire d'un dé le long de l'axe du jet, en rayons. Reste sous le pas
         * des rangs : la poignée respire sans que deux dés se retrouvent l'un dans l'autre. */
        private const val THROW_TRAIL_JITTER = 0.2f

        /**
         * Part du décalage latéral que chaque dé conserve dans sa visée. À 0 toute la poignée
         * converge vers un même point et arrive en tas ; à 1 les dés filent en parallèle et
         * les plus excentrés ratent la piste.
         */
        private const val THROW_AIM_KEEP = 0.6f

        /**
         * Recul du point de lâcher au-delà du bord du plateau, en rayons. Passé un rayon le
         * dé est hors piste : il y entre en volant au lieu d'y apparaître.
         */
        private const val THROW_ENTRY_RADII = 1.4f

        /**
         * Étendue de la zone visée au centre du plateau, en fraction de ses dimensions : le
         * cap du jet pointe vers un point tiré là-dedans. Assez large pour que deux lancers
         * du même côté ne se ressemblent pas, assez serré pour qu'une poignée lâchée d'un
         * flanc entre bien dans la piste au lieu de la longer.
         */
        private const val THROW_AIM_SPREAD = 0.30f

        /**
         * Prise du feutre, tirée **une fois par lancer** et partagée par toute la poignée :
         * c'est une propriété de la piste et du geste, pas de chaque dé. Elle multiplie la
         * résistance au roulement ([resistRolling]) et fait donc varier la durée d'un jet à
         * l'autre sans désynchroniser les dés d'un même jet.
         */
        private const val MIN_GRIP = 0.75f
        private const val MAX_GRIP = 1.35f

        /** Ce qui reste de dispersion d'un dé à l'autre : la prise n'est jamais identique (±4 %). */
        private const val DIE_GRIP_JITTER = 0.08f

        /** En dessous de cette vitesse, le dé n'a plus d'élan et se pose. */
        private const val STOP_SPEED_FRACTION = 0.07f

        /**
         * Le dé doit aussi avoir cessé de culbuter, exprimé en multiples de la vitesse de
         * rotation d'un dé qui roulerait à [STOP_SPEED_FRACTION]. Sans ce second critère, une
         * collision qui coupe net la translation fige un dé encore en pleine rotation.
         */
        private const val STOP_SPIN_FRACTION = 1f

        private fun stopSpinFor(stopSpeed: Float, rollRadius: Float): Float =
            STOP_SPIN_FRACTION * stopSpeed / rollRadius

        /** Hauteur du centre d'un dé de rayon [dieRadius] posé dans l'orientation donnée. */
        internal fun groundHeightFor(
            die: DisplayDie,
            orientation: Quaternion,
            dieRadius: Float
        ): Float = die.mesh.supportHeight(orientation) * dieRadius

        /**
         * Rebond et frottement des parois et des dés entre eux. Les impulsions étant appliquées
         * au point de contact et non aux centres, le frottement y communique une rotation : un
         * dé qui frotte le long d'un mur ou percute un voisin de biais repart en tournant.
         *
         * Le rebord est du bois : un dé y rebondit à 0,5 environ (mesuré par Kapitaniak et al.,
         * « The three-dimensional dynamics of the die throw », Chaos 22, 2012). À 0,93, la
         * valeur d'origine, il en repartait comme une balle de caoutchouc.
         */
        private const val WALL_RESTITUTION = 0.5f
        private const val WALL_FRICTION = 0.35f
        private const val DIE_RESTITUTION = 0.6f
        private const val DIE_FRICTION = 0.35f

        /**
         * Réglages du **ressenti** des chocs. Ils ne participent à aucun calcul de mouvement : la
         * mécanique est strictement celle d'avant, on ne fait qu'y lire des grandeurs déjà
         * calculées. C'est le seul endroit à toucher pour ajuster les vibrations — jamais la
         * physique.
         *
         * [IMPACT_REFERENCE_FACTOR] est la vitesse de choc qui vaut pleine force, exprimée en
         * fraction de la plus petite dimension du plateau. Elle est calée un cran au-dessus de
         * l'atterrissage typique — une chute de quatre à cinq rayons sous [GRAVITY_FACTOR] arrive
         * à environ 1,3 fois cette dimension par seconde : le premier contact se sent franchement
         * sans saturer, et il reste de la marge au-dessus pour les vrais coups de boutoir.
         *
         * Toutes les mesures passées à [recordImpact] sont des **vitesses d'approche**, jamais des
         * impulsions : celles-ci valent déjà près du double du choc à cause de la restitution, et
         * les mélanger faisait saturer tous les contacts de paroi.
         */
        private const val IMPACT_REFERENCE_FACTOR = 1.4f

        /** En deçà, le choc ne se sentirait pas : rien n'est émis. */
        private const val MIN_IMPACT_STRENGTH = 0.06f

        /** Le bord claque, les dés cliquettent, le feutre étouffe : à vitesse égale, pas à force égale. */
        private const val FELT_IMPACT_GAIN = 1.0f
        private const val WALL_IMPACT_GAIN = 1.3f
        private const val DIE_IMPACT_GAIN = 1.15f

        /**
         * Résorption d'un chevauchement entre dés : temps de réponse, et vitesse maximale de
         * dégagement (en rayons par seconde). Deux dés se repoussent, ils ne se téléportent
         * pas — et la borne dure garantit qu'aucun chevauchement, si profond soit-il, ne se
         * traduit par un saut à l'écran.
         *
         * Exprimées **par seconde** et non par pas : les valeurs d'origine (0,4 de reprise et
         * 0,12 rayon de correction par pas de 1/60 s) valaient tant que le pas était l'image.
         * Le pas est maintenant quatre fois plus fin, et les garder telles quelles aurait
         * quadruplé la vitesse de séparation à l'écran.
         */
        private const val SEPARATION_RELAXATION_RATE = 30.65f
        private const val MAX_SEPARATION_SPEED = 7.2f

        /**
         * Frottement de Coulomb du feutre. C'est lui qui fait rouler le dé au lieu de le faire
         * patiner : la force ne s'oppose pas au déplacement du dé mais au **patinage de son
         * point de contact**, si bien qu'un dé qui roule sans glisser n'en subit rien — et
         * c'est bien pour ça qu'il roule loin. Elle est bornée par la charge, comme tout
         * frottement sec : un dé à peine posé n'accroche presque pas, un dé qui claque sur le
         * feutre accroche franchement, et c'est ce qui le fait partir de travers quand il
         * retombe sur un coin.
         */
        private const val FELT_FRICTION = 0.45f

        /**
         * Résistance au roulement du feutre, en multiples de la charge × rayon
         * ([resistRolling]). **C'est le levier de durée du lancer** : le frottement de Coulomb,
         * seul, n'arrête jamais un dé qui roule sans glisser, puisque son point de contact ne
         * patine pas. Le baisser allonge la course et la durée ensemble.
         */
        private const val ROLL_RESISTANCE = 0.02f

        /**
         * Frottement de pivotement : ce que le feutre oppose à un dé qui tourne à plat sur
         * lui-même. Plus fort que la résistance au roulement — une face entière frotte sur le
         * tapis, là où une arête n'a qu'une ligne de contact — sans quoi un dé posé continue de
         * tourner sur place bien après s'être couché.
         */
        private const val PIVOT_RESISTANCE = 0.05f

        /**
         * Passes du solveur de contact. Un dé couché touche le feutre par trois ou quatre
         * sommets, dont la charge se répartit d'autant mieux qu'on repasse dessus. Il en faut
         * assez pour qu'un appui inutile puisse se décharger jusqu'à zéro — c'est ce qui fait
         * basculer un dé posé de travers — sans que le pas de simulation y passe la journée.
         */
        private const val CONTACT_ITERATIONS = 4

        /**
         * Intensité du coup de pouce donné à un dé en équilibre ([nudgeOffBalance]), en
         * multiples de `g / r`. Il ne s'applique qu'à un dé déjà arrêté et pas encore couché :
         * assez fort pour le faire tomber en deux ou trois dixièmes de seconde, ce qui est le
         * temps que met un vrai dé posé de travers à se décider.
         */
        private const val BALANCE_NUDGE = 3f

        /** En deçà, un patinage ou un ballant n'est plus qu'un résidu de calcul. */
        private const val SLIP_EPSILON = 1e-3f
        private const val SPIN_EPSILON = 1e-4f

        /**
         * Enfoncement laissé au dé, en rayons : en deçà, le feutre le garde sans le repousser.
         * Un millième de rayon est invisible à l'écran, et c'est ce qui évite qu'une correction
         * de position lui rende, pas après pas, l'énergie potentielle de sa propre bascule.
         */
        private const val PENETRATION_SLOP = 1e-3f

        /** Normale du feutre : le plateau est le plan z = 0, l'axe z monte vers le spectateur. */
        private val UP = Vec3(0f, 0f, 1f)

        /** Normales des parois, tournées vers l'intérieur du plateau (y descend vers le joueur). */
        private val LEFT_WALL = Vec3(1f, 0f, 0f)
        private val RIGHT_WALL = Vec3(-1f, 0f, 0f)
        private val BACK_WALL = Vec3(0f, 1f, 0f)
        private val FRONT_WALL = Vec3(0f, -1f, 0f)

        /**
         * Gravité, en fraction de la plus petite dimension de l'arène par seconde².
         *
         * C'est **le** réglage qui décide si un dé roule ou s'il vole. Un dé qui culbute à la
         * vitesse `v` ne reste collé au feutre que tant que `v² / r < g` : sous cette vitesse
         * il pivote sur ses arêtes, au-dessus il décolle à chaque bascule. À 6 — la valeur
         * d'origine, choisie pour la lisibilité de la chute — le seuil tombait si bas que les
         * dés passaient **de 36 % (d12) à 70 % (d4) du lancer en l'air** : on ne voyait plus
         * rouler un polyèdre, on voyait flotter un caillou en apesanteur.
         *
         * Depuis le passage au solveur de contact, `g` n'a plus de contrepartie à renormaliser :
         * le coût d'une culbute et le couple qui couche le dé ne sont plus des barèmes réglés à
         * la main mais des conséquences du contact, et ils suivent donc la pesanteur d'eux-mêmes.
         */
        private const val GRAVITY_FACTOR = 2.4f

        /**
         * Rebond sur le feutre. Le feutre d'une piste de dés absorbe : à 0,45 il renvoyait
         * comme du bois.
         */
        private const val BOUNCE_RESTITUTION = 0.40f

        /**
         * Ce qui distingue un **choc** d'un simple appui, en pas d'intégration de chute libre :
         * un sommet qui descend moins vite que ça se pose sur le feutre, il n'y rebondit pas.
         * Sans cette coupure, un dé au repos rebondirait sur la vitesse que la pesanteur lui
         * donne à chaque pas et frémirait indéfiniment ; trop large, elle rend tous les contacts
         * parfaitement mous et le dé ne saute plus du tout.
         */
        private const val RESTING_STEPS = 6f

        /**
         * Hauteur maximale d'un bond, en rayons de dé. Garde-fou visuel : la conservation de
         * l'énergie interdit déjà à un dé de s'élever plus haut que sa course ne le permet,
         * mais un contact rasant mal résolu pourrait le catapulter, et un dé qui sort du
         * plateau ne revient pas.
         *
         * À 2 rayons, le dé s'élevait d'une pleine largeur de dé au-dessus du feutre et y
         * restait plus d'une demi-seconde : le saut ne se lisait plus comme un rebond mais
         * comme un vol plané.
         *
         * Ce plafond doit rester **au-dessus** du plus haut rebond honnête, sans quoi il cesse
         * d'être un filet pour devenir un réglage de rebond. Depuis que la poignée est jetée
         * vers le bas ([THROW_DOWNWARD]), l'arrivée la plus violente que la géométrie autorise
         * — chute maximale et jet maximal — renvoie le centre à 0,60 rayon : à 0,6 le filet
         * tranchait donc dans le vif. Il est calé une demi-fois au-dessus.
         */
        private const val MAX_HOP_RADII = 0.9f

        /**
         * Part de la barrière de culbute retenue pour dire qu'un dé **a l'air posé**
         * ([tumbleSpeed]). Ce n'est pas le seuil physique de la culbute — un dé lancé emporte
         * aussi de l'élan de rotation, et il culbute encore bien en dessous — mais celui à
         * partir duquel il ne repartira visiblement plus : environ deux rayons par seconde pour
         * un d6, soit trois fois le seuil d'immobilité. Purement descriptif : plus aucune
         * mécanique ne s'y accroche.
         */
        private const val TUMBLE_ENERGY_SHARE = 0.033f

        /**
         * Tolérance d'assiette au repos : cosinus de l'angle maximal entre la face du
         * dessous et le feutre (≈ 2,5°). Sert de critère d'arrêt, voir [isLyingFlat].
         */
        private const val FLAT_COSINE = 0.99905f

        /**
         * Hauteur de lâcher du dé, en multiples de son rayon.
         *
         * Elle se lit avec [THROW_DOWNWARD] : à elles deux elles fixent la durée de la chute
         * initiale, qui est du temps où **rien ne se passe**. À 3,4–5,6 rayons lâchés à vitesse
         * nulle, le dé descendait encore 0,47 s après être entré dans le plateau, sur un lancer
         * de 2,06 s — un quart de l'animation passé à le regarder tomber.
         *
         * Le plancher compte autant que le plafond : un dé qui atteint le niveau du feutre
         * **avant** d'avoir franchi le bord y perd toute sa vitesse verticale (voir
         * [integrateRoll], branche hors piste) et entre alors à plat, sans arc ni rebond. Il
         * faut donc de quoi tenir en l'air le temps de l'entrée — environ 2,6 rayons pour le
         * jet le plus mou.
         */
        private const val MIN_DROP_FACTOR = 2.8f
        private const val MAX_DROP_FACTOR = 4.0f

        /**
         * Vitesse de descente au lâcher, en multiples de `sqrt(g·r)` — la vitesse qu'un dé
         * gagne en tombant d'un demi-rayon.
         *
         * **Une poignée de dés est jetée, pas déposée.** Sans ce terme, le dé part immobile et
         * la chute se paie en `sqrt(2h/g)` : la raccourcir demandait de lâcher presque au ras
         * du feutre, ce qui supprimait l'arc de l'entrée en même temps que l'attente.
         *
         * Avec lui, la chute se fait en `(sqrt(v² + 2gh) − v)/g`. C'est **la durée** qu'il
         * achète, pas la violence : de 4,5 rayons lâchés à l'arrêt le dé arrivait déjà à
         * 13,9 rayons par seconde, et de 3,4 rayons jetés à `1,4·sqrt(g·r)` il arrive à 13,7 —
         * en deux fois moins de temps. Le rebond, lui, vient d'ailleurs : du pas coupé à
         * l'instant du contact ([integrateRoll]), qui ne rend plus au dé l'énergie de son
         * propre enfoncement, et de [MAX_HOP_RADII] relevé au-dessus du plus haut bond honnête.
         */
        private const val THROW_DOWNWARD = 1.4f

        /** Culbutage libre au lancer, en multiples de la vitesse de roulement initiale. */
        private const val MIN_TUMBLE_FACTOR = 0.5f
        private const val MAX_TUMBLE_FACTOR = 1.2f

        /** Écartement des dés au repos, en multiples du rayon. */
        const val SETTLE_PITCH_FACTOR = 2.6f

        /** Disposition en grille la plus carrée possible. */
        fun gridColumns(count: Int): Int = ceil(sqrt(count.toFloat())).toInt().coerceAtLeast(1)

        /**
         * Vitesse angulaire du roulement sans glissement : axe perpendiculaire à la vitesse
         * dans le plan du plateau, |ω| = |v|/r — la face tournée vers la caméra avance dans le
         * sens du déplacement, comme un vrai dé qui roule.
         *
         * Ne sert plus qu'à **lancer** le dé, pour qu'il quitte la main en roulant déjà. Rien
         * ne l'y ramène ensuite : sur le feutre, c'est le frottement au point de contact qui
         * accorde la rotation à la course, et il le fait sans jamais l'imposer.
         */
        internal fun rollingOmega(vx: Float, vy: Float, rollRadius: Float): Vec3 =
            if (rollRadius < 1e-3f) Vec3.ZERO else Vec3(-vy, vx, 0f) * (1f / rollRadius)

        /** Rotation accumulée pendant [dt] à la vitesse angulaire [omega]. */
        internal fun spinRotation(omega: Vec3, dt: Float): Quaternion {
            val rate = omega.length
            if (rate < 1e-3f) return Quaternion.IDENTITY
            return Quaternion.fromAxisAngle(omega, rate * dt)
        }

        /**
         * Face à valeur dont la normale pointe le plus vers la caméra : celle sur laquelle le dé
         * va se coucher, et donc celle qui porte le résultat. Sur un solide à faces opposées,
         * c'est exactement la face du dessous retournée — la valeur se lit d'en haut.
         */
        fun landedFace(die: DisplayDie, current: Quaternion): Face =
            die.mesh.valueFaces.maxBy { current.rotate(it.normal).z }

        /**
         * Rotation minimale depuis [current] amenant la normale de [face] vers la caméra
         * (0,0,1). Son axe est dans le plan du plateau : le dé bascule sur sa face sans
         * jamais pivoter à plat sur lui-même.
         */
        fun settleOrientationOnto(face: Face, current: Quaternion): Quaternion {
            val currentNormal = current.rotate(face.normal)
            return (Quaternion.rotationBetween(currentNormal, Vec3(0f, 0f, 1f)) * current).normalized()
        }
    }
}
